import memoize from "memoize-one";
import { ColDef, ColGroupDef, MenuItemDef } from "ag-grid-community";
import { Iterable, List, Set as ImmutableSet } from "immutable";
import { formatMessage } from "lib/globalize-util";
import lodashSet from "lodash/set";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import { Door, GridParamType, Kinds, ValueTypes } from "constants/product-collection";
import { IDisplayFields, ISchemaKey } from "helpers/product-collection";
import { IAllocationDimensionValue, ROLLUP_ALLOCATION_DIMENSION_DELIMITER } from "helpers/allocation-dimension";
import { ValueOf } from "lib/ts-utility";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import {
    IFilterConditionQueryParam,
    IGridParamsQueryParam,
} from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/data-view-panel/grid-param-helpers";
import { TotalTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";

import { nullish } from "./nullish";
import { CopyCutPasteActions, UnitsPath } from "./product-collection/units-copy-paste";
import { Metrics } from "helpers/metrics/types";
import {
    IAttributeFilters,
    IDimensionFilter,
} from "components/product-collection/product-collection-toolbar/product-collection-toolbar-filters/types";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { getLegacyTypeByMetricsKind, getMetricKindByLegacyType, getTotalTypesLabel } from "./metrics/legacy-metrics";
import { getInitialState as getDimensionsPanelEmptyState } from "reducers/dimensions-panel/applied/initial-state";
import { customSort } from "./array-utils";

export interface IDoorTotal {
    fieldName: string;
    currency: {
        symbol: string;
        decimal_places: number;
    };
    formula?: string;
}

export interface IDoor {
    id: string;
    value: string;
    label: string;
    classifications:
        | {
              [classification: string]: string;
          }
        | {};
    retail?: IDoorTotal;
    wholesale?: IDoorTotal;
    totalConversionRate?: number;
    doorNotes?: string;
    groupedDoorsNumber?: number;
}
export interface IAllocationDimensionViewConfig {
    // `undefined` means no config specified (Use default)
    dimensionId: string;
    values: string[] | undefined;
    direction: Order | undefined;
}

export type IAllocationDimensionColumns = IAllocationDimensionViewConfig[];

export interface IDoorGroup {
    field: string;
    values: string[];
    hiddenValues?: string[];
    direction: Order;
}

export interface IClasificationHierarchy {
    field: string;
    value: string;
}

export const SR_CUSTOM_DOOR_GROUPING_DELIMITER = ROLLUP_ALLOCATION_DIMENSION_DELIMITER;
export const SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER = "===";
export const GROUP_ID_DELIMITER = "_";

const COL_ID_DELIMITER = "_";

export const parseDimensionColumn = (colId: string, deliveryIds: string[]) => {
    if (colId.startsWith(ColumnTypes.DoorBulkQuantity)) {
        const [node1, node2] = colId.replace(ColumnTypes.DoorBulkQuantity, "").split(COL_ID_DELIMITER).splice(1);

        const hasDelivery = !nullish(node2);
        const doorId = hasDelivery ? node2 : node1;
        const deliveryId = hasDelivery ? node1 : undefined;
        return { doorId, deliveryId };
    }

    if (colId.startsWith(ColumnTypes.AllDimensionTotal)) {
        return { isAllDimensionTotal: true };
    }

    if (colId.startsWith(ColumnTypes.AllocationDimensionTotal)) {
        const [deliveryIndex] = colId
            .replace(ColumnTypes.AllocationDimensionTotal, "")
            .split(COL_ID_DELIMITER)
            .splice(1);
        return { isDimensionTotal: true, deliveryId: deliveryIds[deliveryIndex] };
    }

    return null;
};

export const PURCHASE_ORDER_DOOR_GROUP_FIELD = "purchase_order_door_group";

export const BLANK_CLASSIFICATION_VALUE = "na";
/**
 * Merges display properties' field names and fields' values into an object
 */
export const getClassifications = (displayProperties: ISchemaKey[], fields) => {
    return displayProperties.reduce((acc, displayProperty) => {
        if (displayProperty.kind !== Kinds.ALLOCATION_DIMENSION_CLASSIFICATION) {
            return acc;
        }

        const fieldName = displayProperty.collectionId
            ? `${displayProperty.field_name}${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}${displayProperty.collectionId}`
            : displayProperty.field_name;

        const classificationValue =
            displayProperty.value_type === ValueTypes.NUMBER
                ? lodashGet(fields, [fieldName, "number_value"], undefined)
                : lodashGet(fields, [fieldName, "string_value"], "").trim();

        if (classificationValue) {
            acc[fieldName] = classificationValue;
        }
        return acc;
    }, {});
};

export const amountOfNotTotalChildren = (node: ColGroupDef | ColDef) => {
    const { children } = node as ColGroupDef;
    const TOTALS_TYPE_SET = ImmutableSet<string>([
        ColumnTypes.AllDimensionTotal,
        ColumnTypes.ClassificationTotal,
        ColumnTypes.AllocationDimensionTotal,
    ]);
    return (
        children?.reduce((acc, curr: ColDef | ColGroupDef) => {
            const currAsColDef = curr as ColDef;
            const currAsColGroupDef = curr as ColGroupDef;
            const typeOrGroupId = currAsColDef.type || currAsColGroupDef.groupId;
            if (TOTALS_TYPE_SET.has(typeOrGroupId as string)) {
                return acc;
            }
            return acc + 1;
        }, 0) || 0
    );
};

export const wrapChildrenWithNoSiblings = (node: ColGroupDef | ColDef) => {
    const nodeAsGroup = node as ColGroupDef;
    const { children } = nodeAsGroup;
    const notTotalChildren = amountOfNotTotalChildren(node);
    if (notTotalChildren === 1) {
        const onlyChild = children[children.length - 1];
        const grandchilds = (onlyChild as ColGroupDef).children;
        if (grandchilds) {
            nodeAsGroup.children = grandchilds;
            wrapChildrenWithNoSiblings(node);
        }
    } else if (notTotalChildren > 1) {
        children.forEach(wrapChildrenWithNoSiblings);
    }
};
export const MENU_GENERAL_TAB_ONLY = ["generalMenuTab"];
interface IAllocationDimensionMenuItemsParams {
    enablePaste: boolean;
    path: UnitsPath;
    actionCallback: (u: UnitsPath, c: CopyCutPasteActions) => void;
}

export const getAllocationDimensionMenuItems = ({
    enablePaste,
    path,
    actionCallback,
}: IAllocationDimensionMenuItemsParams): MenuItemDef[] => [
    {
        name: formatMessage("Copy Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Copy),
    },
    {
        name: formatMessage("Cut Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Cut),
    },
    {
        name: formatMessage("Paste Units"),
        action: actionCallback.bind(null, path, CopyCutPasteActions.Paste),
        disabled: !enablePaste,
    },
];
export enum Order {
    Ascending = "asc",
    Descending = "desc",
    Custom = "custom",
}
export const parseDoorCollection = memoize(
    (
        doorsCollection: IAllocationDimensionValue[] | List<IAllocationDimensionValue>,
        displayProperties: ISchemaKey[] | List<ISchemaKey>,
        formulaFields: IDisplayFields,
    ): IDoor[] => {
        const normalisedDoorsCollection: IAllocationDimensionValue[] = Iterable.isIterable(doorsCollection)
            ? (doorsCollection as List<IAllocationDimensionValue>).toJS()
            : doorsCollection || [];
        const normalisedDisplayProperties = Iterable.isIterable(displayProperties)
            ? (displayProperties as List<ISchemaKey>).toJS()
            : displayProperties;
        const doors = [];

        normalisedDoorsCollection.forEach((door) => {
            if (door.included && door.active) {
                const fields = door.fields;
                const retailFieldName = lodashGet(door, "fields.allocation_retail_price.string_value");
                const wholesaleFieldName = lodashGet(door, "fields.allocation_cost_price.string_value");
                const totalConversionRate = !isNaN(Number(lodashGet(door, "fields.total_conversion_rate.string_value")))
                    ? Number(lodashGet(door, "fields.total_conversion_rate.string_value")) === 0
                        ? 1
                        : Number(lodashGet(door, "fields.total_conversion_rate.string_value"))
                    : 1;
                const groupedDoorsNumber = lodashGet(door, Door.FIELDS.NUMBER_OF_DOORS)
                    ? +lodashGet(door, Door.FIELDS.NUMBER_OF_DOORS)
                    : undefined;
                const doorNotes = lodashGet(door, "fields._door_notes.string_value");
                doors.push({
                    id: door.id,
                    label: door.name,
                    value: door.external_id,
                    retail: {
                        fieldName: retailFieldName,
                        currency: {
                            symbol: "$",
                            decimal_places: 2,
                        },
                        field: formulaFields[retailFieldName] ? formulaFields[retailFieldName].field.toJS() : undefined,
                        formula: formulaFields[retailFieldName]
                            ? formulaFields[retailFieldName].field.get("formula")
                            : undefined,
                    },
                    wholesale: {
                        fieldName: wholesaleFieldName,
                        currency: {
                            symbol: "$",
                            decimal_places: 2,
                        },
                        field: formulaFields[wholesaleFieldName]
                            ? formulaFields[wholesaleFieldName].field.toJS()
                            : undefined,
                        formula: formulaFields[wholesaleFieldName]
                            ? formulaFields[wholesaleFieldName].field.get("formula")
                            : undefined,
                    },
                    classifications: getClassifications(normalisedDisplayProperties, fields),
                    totalConversionRate,
                    doorNotes,
                    groupedDoorsNumber,
                });
            }
        });

        return doors;
    },
);

export const DOOR_GROUP_QUERY_KEY = "dg";
export enum DoorGroupParams {
    Show = "s",
    Classification = "c",
    ClassificationLabel = "cl",
    Expand = "e",
    ClassificationDir = "dir",
    ClassificationHide = "h",
    AllocationIds = "a",
    Delimiter = "d",
    PlanMetrics = "pm",
    ShowPlanMetrics = "spm",
    ShowTotals = "st",
    ActiveValues = "dc",
    DoorsSortDir = "dcs",
    Metrics = "metrics",
    ActiveAttributes = "aa",
}

export interface IDoorGroupsQueryParams {
    [DoorGroupParams.Show]?: 0 | 1;
    [DoorGroupParams.Classification]?: string;
    [DoorGroupParams.ClassificationLabel]?: string;
    [DoorGroupParams.Expand]?: {
        [groupId: string]: 1 | 0;
    };
    [DoorGroupParams.ClassificationDir]?: Order;
    [DoorGroupParams.ClassificationHide]?: string[];
    [DoorGroupParams.AllocationIds]?: string[];
    [DoorGroupParams.Delimiter]?: string;
    [DoorGroupParams.PlanMetrics]?: string[];
    [DoorGroupParams.ShowTotals]?: boolean;
    [DoorGroupParams.ShowPlanMetrics]?: boolean;
    [DoorGroupParams.Metrics]?: Metrics;
    [DoorGroupParams.ActiveValues]?: IAllocationDimensionColumns;
    [DoorGroupParams.ActiveAttributes]?: IDoorGroup[];
}
// Classification Here means attributes
export enum DimensionParamsActionType {
    ShowDoorsGroup = "showDoorsGroup",
    ShowClassification = "showClassification",
    ShowClassificationLabel = "showClassificationLabel",
    ExpandGroup = "expandGroup",
    CollapseGroup = "collapseGroup",
    ChangeDirection = "changeDirection",
    HideClassification = "hideClassificaton",
    SetAllocationIds = "setAllocationIds",
    SetDelimiter = "setDelimiter",
    SelectPlanMetric = "selectPlanMetric",
    ShowDoorTotals = "showDoorTotals",
    ShowPlanMetrics = "showPlanMetrics",
    SetMetrics = "setMetrics",
    SetActiveAttributes = "setActiveAttributes",
    SetActiveValues = "setActiveValues",
}

// tslint:disable-next-line: interface-over-type-literal
export type DimensionParamsAction = {
    [DimensionParamsActionType.ShowDoorsGroup]: {
        type: DimensionParamsActionType.ShowDoorsGroup;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.ExpandGroup]: {
        type: DimensionParamsActionType.ExpandGroup;
        value: string;
        inUrl?: boolean;
    };
    [DimensionParamsActionType.CollapseGroup]: {
        type: DimensionParamsActionType.CollapseGroup;
        value: string;
    };
    [DimensionParamsActionType.ShowClassification]: {
        type: DimensionParamsActionType.ShowClassification;
        value: string | undefined;
    };
    [DimensionParamsActionType.ShowClassificationLabel]: {
        type: DimensionParamsActionType.ShowClassificationLabel;
        value: string | undefined;
    };
    [DimensionParamsActionType.HideClassification]: {
        type: DimensionParamsActionType.HideClassification;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.ChangeDirection]: {
        type: DimensionParamsActionType.ChangeDirection;
        value: Order | undefined;
    };
    [DimensionParamsActionType.SetAllocationIds]: {
        type: DimensionParamsActionType.SetAllocationIds;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.SetDelimiter]: {
        type: DimensionParamsActionType.SetDelimiter;
        value: string | undefined;
    };
    [DimensionParamsActionType.SelectPlanMetric]: {
        type: DimensionParamsActionType.SelectPlanMetric;
        value: string[] | undefined;
    };
    [DimensionParamsActionType.ShowDoorTotals]: {
        type: DimensionParamsActionType.ShowDoorTotals;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.SetMetrics]: {
        type: DimensionParamsActionType.SetMetrics;
        value: Metrics | undefined;
    };
    [DimensionParamsActionType.ShowPlanMetrics]: {
        type: DimensionParamsActionType.ShowPlanMetrics;
        value: boolean | undefined;
    };
    [DimensionParamsActionType.SetActiveAttributes]: {
        type: DimensionParamsActionType.SetActiveAttributes;
        value: IDoorGroup[] | undefined;
    };
    [DimensionParamsActionType.SetActiveValues]: {
        type: DimensionParamsActionType.SetActiveValues;
        value: IAllocationDimensionColumns | undefined;
    };
};

export type ISaveStateToDimensionParamsActions = ValueOf<DimensionParamsAction> | Array<ValueOf<DimensionParamsAction>>;

/**
 * This function is an intermediate function to make existing way of configuring door group params work with `DimensionsPanelReduxState`
 * Removing it will require big effort to replace the usage with something else
 * @param saveStateToQueryParamsActions
 * @param existingState
 * @returns
 */
export const processActionsToDimensionParams = (
    saveStateToQueryParamsActions: ISaveStateToDimensionParamsActions,
    existingState?: DimensionsPanelReduxState,
) => {
    const actions = Array.isArray(saveStateToQueryParamsActions)
        ? saveStateToQueryParamsActions
        : [saveStateToQueryParamsActions];
    let mutatedState = lodashCloneDeep(existingState ?? getDimensionsPanelEmptyState());
    actions.forEach((action) => {
        switch (action.type) {
            case DimensionParamsActionType.ShowDoorsGroup:
                mutatedState = lodashSet(mutatedState, "showDoors", action.value ? true : false);
                break;
            case DimensionParamsActionType.ShowClassification:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.id === action.value) {
                        attribute.selected = true;
                        attribute.values.forEach((attributeValue) => {
                            attributeValue.selected = true;
                        });
                    } else {
                        attribute.selected = false;
                        attribute.values.forEach((attributeValue) => {
                            attributeValue.selected = false;
                        });
                    }
                });
                break;
            case DimensionParamsActionType.ShowClassificationLabel:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.id === action.value) {
                        attribute.showLabelInHeader = true;
                    } else {
                        attribute.showLabelInHeader = false;
                    }
                });
                break;
            case DimensionParamsActionType.CollapseGroup:
                mutatedState.collapsedGroupIds = mutatedState.collapsedGroupIds
                    .filter((groupId) => groupId !== action.value)
                    .concat([action.value]);
                break;
            case DimensionParamsActionType.ExpandGroup:
                mutatedState.collapsedGroupIds = mutatedState.collapsedGroupIds.filter(
                    (groupId) => groupId !== action.value,
                );
                break;
            case DimensionParamsActionType.ChangeDirection:
                if (action.value) {
                    mutatedState.attributes.forEach((attribute) => {
                        attribute.sort = action.value;
                    });
                }
                break;
            case DimensionParamsActionType.HideClassification:
                mutatedState.attributes.forEach((attribute) => {
                    if (attribute.selected) {
                        attribute.values.forEach((attributeValue) => {
                            if (!(action.value ?? []).includes(attributeValue.id)) {
                                attributeValue.selected = true;
                            } else {
                                attributeValue.selected = false;
                            }
                        });
                    }
                });
                break;
            case DimensionParamsActionType.SetDelimiter:
                mutatedState.delimiter = action.value || GROUP_ID_DELIMITER;
                break;
            case DimensionParamsActionType.SelectPlanMetric:
                // Legacy Metrics. We'll set values, subtotal and totals ON
                if (action.value && action.value.length) {
                    mutatedState.metrics.showAssortmentPlan = true;
                    mutatedState.metrics.values = action.value.reduce((acc, legacyMetricType) => {
                        const metricKind = getMetricKindByLegacyType(legacyMetricType as TotalTypes);
                        if (metricKind) {
                            // We only want to set this to `true` when there is actual metric
                            acc.push({
                                showValue: true,
                                showTotals: true,
                                showSubtotals: true,
                                kind: metricKind,
                                displayName: getTotalTypesLabel(legacyMetricType as TotalTypes),
                                isTargetMetric: false,
                            });
                        }
                        return acc;
                    }, []);
                } else {
                    mutatedState.metrics.showAssortmentPlan = false;
                }
                break;
            case DimensionParamsActionType.ShowDoorTotals:
                mutatedState.metrics.showAssortmentTotals = !!action.value;
                break;
            case DimensionParamsActionType.SetMetrics:
                mutatedState.metrics.values = action.value;
                break;
            case DimensionParamsActionType.ShowPlanMetrics:
                mutatedState.metrics.showAssortmentPlan = action.value;
                break;
            case DimensionParamsActionType.SetActiveValues:
                action.value.forEach((activeValueView, idx) => {
                    if (mutatedState.dimensions[idx]) {
                        // This will be mutated if any of the value is selected
                        mutatedState.dimensions[idx].selected = false;
                        mutatedState.dimensions[idx].sort = activeValueView.direction ?? Order.Ascending;

                        mutatedState.dimensions[idx].values.forEach((dimensionValue) => {
                            if (!activeValueView.values) {
                                // When `activeValueView.values` is falsy, treat it as all dimension values being ON
                                dimensionValue.selected = true;
                            } else if (activeValueView.values.length === 0) {
                                // When `activeValueView.values` is empty array, treat it as all dimension values being FALSE
                                dimensionValue.selected = false;
                            } else if (activeValueView.values) {
                                dimensionValue.selected = activeValueView.values.includes(dimensionValue.id);
                            }

                            if (dimensionValue.selected) {
                                mutatedState.dimensions[idx].selected = true;
                            }
                        });

                        if (mutatedState.dimensions[idx].sort === Order.Custom && activeValueView.values) {
                            // Sort values based on passed-in values. This is not an ideal because we loop twice (above block and this block).
                            // It's done this way for readibility. Practically, number of attributes and values will be small, therefore, performance should not be a concern.
                            customSort(
                                mutatedState.dimensions[idx].values,
                                activeValueView.values,
                                (value) => value.id,
                            );
                        }
                    }
                });
                break;
            case DimensionParamsActionType.SetActiveAttributes:
                for (const activeAttributeView of action.value) {
                    const mutatedAttribute = mutatedState.attributes.find(
                        (attribute) => attribute.id === activeAttributeView.field,
                    );
                    if (mutatedAttribute) {
                        // `action.value` always contains active attribute
                        mutatedAttribute.selected = true;
                        mutatedAttribute.sort = activeAttributeView.direction;

                        mutatedAttribute.values = mutatedAttribute.values.map((activeValue) => {
                            if (!activeAttributeView.values) {
                                // When `activeValueView.values` is falsy, treat it as all attribute values being ON
                                activeValue.selected = true;
                            } else if (activeAttributeView.values.length === 0) {
                                // When `activeValueView.values` is empty array, treat it as all attribute values being FALSE
                                activeValue.selected = false;
                            } else if (activeAttributeView.values) {
                                activeValue.selected = activeAttributeView.values.includes(activeValue.id);
                            }

                            return activeValue;
                        });

                        if (mutatedAttribute.sort === Order.Custom && activeAttributeView.values) {
                            // Sort values based on passed-in values. This is not an ideal because we loop twice (above block and this block).
                            // It's done this way for readibility. Practically, number of attributes and values will be small, therefore, performance should not be a concern.
                            mutatedAttribute.values.sort((a, b) => {
                                const indexA = activeAttributeView.values.findIndex((value) => value === a.id);
                                const indexB = activeAttributeView.values.findIndex((value) => value === b.id);

                                if (indexA === -1) {
                                    return 1;
                                }

                                if (indexA < indexB || indexB === -1) {
                                    return -1;
                                }

                                return 0;
                            });
                        }
                    }
                }
                break;
            default:
                break;
        }
    });
    return mutatedState;
};

interface ILegacyParsedDoorGroupParams {
    attribute?: any;
    attributeLabel?: string;
    attributeDirection?: Order;
    attributeHiddenValues?: string[];
    legacyPlanMetrics?: string[];
}

/**
 * Helper function to parse commonly used properties for legacy usage
 * Legacy usage only allows enabling of single attribute
 */
export const legacy_parseDimensionParams = memoize(
    (dimensionParams: DimensionsPanelReduxState): ILegacyParsedDoorGroupParams => {
        const singleActiveAttribute = dimensionParams.attributes.find((attribute) => attribute.selected);
        const attributeHiddenValues =
            singleActiveAttribute?.values
                .filter((attributeValue) => !attributeValue.selected)
                .map((attributeValue) => attributeValue.id) ?? [];
        const attributeLabel = dimensionParams.attributes.find((attribute) => attribute.showLabelInHeader)?.id;
        const attributeDirection = singleActiveAttribute?.sort;
        const legacyPlanMetrics = dimensionParams.metrics.values.map((metric) =>
            getLegacyTypeByMetricsKind(metric.kind),
        );

        return {
            attribute: singleActiveAttribute?.id,
            attributeLabel,
            attributeHiddenValues,
            attributeDirection,
            legacyPlanMetrics,
        };
    },
);

/**
 * Filter out door based on date/door drop-down filter and doors grouping dialog filter
 *
 * @param door - Current door being evaluated
 * @param activeClassification - Current door classification category being evaluated
 * @param hiddenClassificationValues - List of hidden classification values
 * @param collectionId - collection id for rollup logic
 * @param attributeFilters - Key - array value object of attribute filters

 *
 * @returns true if classification in hidden list or door not in door-filter (ONLY if door-filter populated)
 */
export const isDoorInClassification = (
    door: IDoor,
    {
        activeClassification,
        hiddenClassificationValues,
        collectionId,
        attributeFilters,
    }: {
        activeClassification: string;
        hiddenClassificationValues: any;
        collectionId?: string;
        attributeFilters?: IAttributeFilters;
    },
): boolean => {
    const activeClassificationValue = door?.classifications?.[activeClassification] || "";

    const attributeFilterKeys = Object.keys(attributeFilters || {});
    const doorHasClassification =
        attributeFilterKeys.length === 0
            ? true
            : attributeFilterKeys.every((key) => attributeFilters[key].includes(door?.classifications?.[key] || ""));

    if (Array.isArray(hiddenClassificationValues)) {
        return !hiddenClassificationValues?.includes(activeClassificationValue) && doorHasClassification; // classification is not hidden
    }

    const classificationKey = collectionId
        ? `${activeClassification}${SR_CUSTOM_DOOR_GROUPING_DELIMITER}${collectionId}`
        : activeClassification;
    return (
        !hiddenClassificationValues?.[classificationKey]?.includes(activeClassificationValue) && doorHasClassification
    );
};

/**
 * Test if dimension values are in filter if filter is populated
 *
 * @param dimensionFilters - filter that contains specified dimensions values
 * @param dimensions - dimension internal-id
 *
 * @returns true if all dimensions internal-ids are in all populated filter
 *
 */
export const isDimensionInFilters = (dimensionFilters: string[][], dimensions: string[]): boolean => {
    let dimensionInFilter = true;
    for (const dimIndex in dimensionFilters) {
        const filter = dimensionFilters?.[dimIndex];
        const dimensionValue = dimensions?.[dimIndex];
        // filter populated and dimension internal-id exists then test if value in filter
        if (filter?.length && dimensionValue) {
            dimensionInFilter = dimensionInFilter && filter.includes(dimensionValue);
        }
    }
    return dimensionInFilter;
};

export const getVisibleDoors = (
    doors: IDoor[],
    dimensionParams: DimensionsPanelReduxState,
    gridParams: IGridParamsQueryParam,
    excludeAttributeFilter: boolean = false,
) => {
    const { attribute, attributeHiddenValues } = legacy_parseDimensionParams(dimensionParams);
    const dimensionFilters = getDimensionFilters(gridParams);
    const attributeFilters = !excludeAttributeFilter && getAttributeFilters(gridParams);

    return doors.filter(
        (door) =>
            isDoorInClassification(door, {
                activeClassification: attribute,
                hiddenClassificationValues: attributeHiddenValues,
                attributeFilters,
            }) && isDimensionInFilters(dimensionFilters, [null, door.id]), // door in selected classifications // and door-dimension in filters, date-dimension unavailable (only in rows/bulks)
    );
};

/**
 * Retrieve dimensionFilter inside of gridParam.filter
 *
 * @param gridParams - Grid parameters containing filter with meta-filter embedded into filterTo field
 * @returns - double array that contains selected external/interal ids indexed by allocation_dimension ids [["0","1"], ["1","2"]]
 */
export const getDimensionFilters = memoize(
    (gridParams: IGridParamsQueryParam): IDimensionFilter[] => {
        // use special column to store meta-filter
        const dimensionFilterString = (
            gridParams?.filter?.[GridParamType.DimensionFilters] as IFilterConditionQueryParam
        )?.filterTo as string;
        // guarantee date/door is not null/undefined
        let dimensionFilter;
        try {
            if (dimensionFilterString) {
                dimensionFilter = JSON.parse(dimensionFilterString);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Cannot parse JSON", e);
        }

        // guarantee non-null/undefined variables
        return dimensionFilter || [];
    },
    (newArgs, oldArgs) => {
        return (
            newArgs[0]?.filter?.[GridParamType.DimensionFilters]?.filterTo ===
            oldArgs[0]?.filter?.[GridParamType.DimensionFilters]?.filterTo
        );
    },
);

/**
 * Retrieve attributeFilter inside of gridParam.filter
 *
 * @param gridParams - Grid parameters containing filter with meta-filter embedded into filterTo field
 * @returns - list of objects that contains selected attributes and its selected values
 */
export const getAttributeFilters = memoize(
    (gridParams: IGridParamsQueryParam): IAttributeFilters => {
        // use special column to store meta-filter
        const attributeFilterString = (
            gridParams?.filter?.[GridParamType.CustomAttributeFilter] as IFilterConditionQueryParam
        )?.filterTo as string;
        // guarantee date/door is not null/undefined
        let attributeFilter;
        try {
            if (attributeFilterString) {
                attributeFilter = JSON.parse(attributeFilterString);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Cannot parse JSON", e);
        }

        // guarantee non-null/undefined variables
        return attributeFilter || {};
    },
    (newArgs, oldArgs) => {
        return (
            newArgs[0]?.filter?.[GridParamType.CustomAttributeFilter]?.filterTo ===
            oldArgs[0]?.filter?.[GridParamType.CustomAttributeFilter]?.filterTo
        );
    },
);

export enum DimensionProps {
    Id = "id",
    Classification = "group",
    ClassificationLabel = "group_label",
    Show = "show",
    ClassificationDir = "sort_direction",
    ClassificationHide = "hidden_group_values",
    PlanMetrics = "plan_metrics",
    ShowTotals = "show_totals",
    ShowPlan = "show_plan",
    ActiveValues = "active_values",
    ActiveAttributes = "active_attributes",
}

export interface IActiveValues {
    values: Array<{ id: string }>;
    order: DimensionOrder;
    all_values?: boolean;
}

export interface IActiveAttributes {
    attributes: Array<{
        field_name: string;
        values: Array<{
            string_value: string;
        }>;
        order: DimensionOrder;
    }>;
    order: DimensionOrder;
}

export enum DimensionOrder {
    Ascending = "ASCENDING",
    Descending = "DESCENDING",
    Custom = "CUSTOM",
    Unordered = "UNORDERED",
}

export enum ExpandedColumnsProps {
    Delimiter = "delimiter",
    Columns = "columns",
}

export interface IDimension {
    [DimensionProps.Id]: string;
    [DimensionProps.Classification]?: string;
    [DimensionProps.ClassificationLabel]?: string;
    [DimensionProps.Show]?: boolean;
    [DimensionProps.ClassificationDir]?: string;
    [DimensionProps.ClassificationHide]?: string[];
    [DimensionProps.PlanMetrics]?: string[];
    [DimensionProps.ActiveValues]?: IActiveValues;
    [DimensionProps.ActiveAttributes]?: IActiveAttributes;
}

export interface IExpandedColumns {
    [ExpandedColumnsProps.Delimiter]?: string;
    [ExpandedColumnsProps.Columns]?: { [key: string]: boolean };
}

export interface IServerDimensionPreference {
    dimensions: IDimension[];
    expanded_columns: IExpandedColumns;
}

export const isDoorColumn = (columnName: string, colType?: string) => {
    return Object.values(ColumnTypes).some((type) => columnName?.includes(type) || colType === type);
};
